import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequirePermission,
} from '@/components/layout/route-guards';
import { RouteErrorBoundary } from '@/components/common/error-boundary';
import { RouteFallback } from '@/components/common/route-fallback';
import { AuthLayout } from '@/routes/auth/auth-layout';
import { ForgotPasswordRoute } from '@/routes/auth/forgot-password';
import { LoginRoute } from '@/routes/auth/login';
import { ResetPasswordRoute } from '@/routes/auth/reset-password';
import { LiveMapRoute } from '@/routes/live-map';

// The map stays in the main bundle: it is one click from the dashboard and the
// heaviest thing to load. Everything else is split out.
const DashboardRoute = lazy(() =>
  import('@/routes/dashboard').then((m) => ({ default: m.DashboardRoute })),
);
const VehicleDetailRoute = lazy(() =>
  import('@/routes/vehicle-detail').then((m) => ({ default: m.VehicleDetailRoute })),
);
const AlertsRoute = lazy(() => import('@/routes/alerts').then((m) => ({ default: m.AlertsRoute })));
const GeofencesRoute = lazy(() =>
  import('@/routes/geofences').then((m) => ({ default: m.GeofencesRoute })),
);
const SettingsRoute = lazy(() =>
  import('@/routes/settings').then((m) => ({ default: m.SettingsRoute })),
);
const DriversRoute = lazy(() =>
  import('@/routes/drivers').then((m) => ({ default: m.DriversRoute })),
);
const TransactionsRoute = lazy(() =>
  import('@/routes/transactions').then((m) => ({ default: m.TransactionsRoute })),
);
const AdminVehiclesRoute = lazy(() =>
  import('@/routes/admin/vehicles').then((m) => ({ default: m.AdminVehiclesRoute })),
);
const AdminDevicesRoute = lazy(() =>
  import('@/routes/admin/devices').then((m) => ({ default: m.AdminDevicesRoute })),
);
const AdminSimsRoute = lazy(() =>
  import('@/routes/admin/sims').then((m) => ({ default: m.AdminSimsRoute })),
);
const AdminUsersRoute = lazy(() =>
  import('@/routes/admin/users').then((m) => ({ default: m.AdminUsersRoute })),
);
const AdminRolesRoute = lazy(() =>
  import('@/routes/admin/roles').then((m) => ({ default: m.AdminRolesRoute })),
);
const AdminAuditRoute = lazy(() =>
  import('@/routes/admin/audit').then((m) => ({ default: m.AdminAuditRoute })),
);
const NotFoundRoute = lazy(() =>
  import('@/routes/not-found').then((m) => ({ default: m.NotFoundRoute })),
);

const suspended = (element: React.ReactNode) => (
  <Suspense fallback={<RouteFallback />}>{element}</Suspense>
);

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/dashboard" replace /> },

  {
    element: <RedirectIfAuthenticated />,
    children: [
      {
        element: <AuthLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: '/login', element: <LoginRoute /> },
          { path: '/forgot-password', element: <ForgotPasswordRoute /> },
          { path: '/reset-password', element: <ResetPasswordRoute /> },
        ],
      },
    ],
  },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: '/dashboard', element: suspended(<DashboardRoute />) },
          { path: '/map', element: <LiveMapRoute /> },
          { path: '/vehicles/:vehicleId', element: suspended(<VehicleDetailRoute />) },
          { path: '/alerts', element: suspended(<AlertsRoute />) },
          { path: '/settings', element: suspended(<SettingsRoute />) },

          {
            element: <RequirePermission permissions={['geofence:read']} />,
            children: [{ path: '/geofences', element: suspended(<GeofencesRoute />) }],
          },

          {
            element: <RequirePermission permissions={['payment:read']} />,
            children: [
              { path: '/drivers', element: suspended(<DriversRoute />) },
              { path: '/transactions', element: suspended(<TransactionsRoute />) },
            ],
          },

          {
            path: '/admin',
            element: <Outlet />,
            children: [
              {
                element: <RequirePermission permissions={['vehicle:read']} />,
                children: [{ path: 'vehicles', element: suspended(<AdminVehiclesRoute />) }],
              },
              {
                element: <RequirePermission permissions={['device:read']} />,
                children: [
                  { path: 'devices', element: suspended(<AdminDevicesRoute />) },
                  { path: 'sims', element: suspended(<AdminSimsRoute />) },
                ],
              },
              {
                element: <RequirePermission permissions={['user:read']} />,
                children: [{ path: 'users', element: suspended(<AdminUsersRoute />) }],
              },
              {
                element: <RequirePermission permissions={['role:read']} />,
                children: [{ path: 'roles', element: suspended(<AdminRolesRoute />) }],
              },
              {
                element: <RequirePermission permissions={['audit:read']} />,
                children: [{ path: 'audit', element: suspended(<AdminAuditRoute />) }],
              },
            ],
          },

          { path: '*', element: suspended(<NotFoundRoute />) },
        ],
      },
    ],
  },
]);
